import React, { useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import NumericEditCell from './NumericEditCell.js';
import { getMostRecentOfferRound, isTownOfferFromPriorRound } from '../../../../utils/mostRecentOfferUtils.js';
import { hasRoundMismatch } from '../config/columnDefinitions.js';
import { convertMVToAV, convertAVToMV } from '../../../../utils/conversionUtils.js';
import { usePersistedDataStore } from '../../../store.js';
import { useUser } from '../../../UserContext.js';
import { createOfferContext } from '../../../../utils/offerContextUtils.js';
import { linkSessionFromUser } from '../../../../utils/townPortalFirstResponseNotify.js';
import { updateOfferWithDialog } from '../services/simpleOfferUpdateWithDialog.js';
import {
  applyOfferRowPatch,
  buildDerivedOfferFieldsForRow,
  buildOfferRowPatchFromCase,
  parseRowOfferValue,
  refreshMunicipalOfferCard,
} from '../services/offerGridUpdateService.js';
import { getRowMaxNegotiationRound } from '../utils/helpers.js';
import { toast } from '../../../../common/toast';
import logger from '../../../../utils/logger';

/**
 * Generalized Offer Cell Component
 * Handles both Aventine and Town offers based on party prop
 * Handles display logic only - editing is handled by OfferEditCell
 */
const OfferCell = ({ 
  row, 
  avToggleActive = false, 
  townView = false, 
  party = 'Aventine',
  field = null
}) => {
  // Auto-determine field from party if not provided
  const offerField = field || (party === 'Aventine' ? 'OfferValue' : 'TownOffer');

  if (townView && party === 'Aventine' && row._townAventineDisplayNotAvailable) {
    return <span style={{ color: '#888', fontStyle: 'italic' }}>Not Available</span>;
  }

  const mostRecentOffer = parseRowOfferValue(row[offerField]);
  
  // For Town offers, check if the most recent offer has a "Withdraw" response
  const isWithdraw = party === 'Town' && (() => {
    if (!row.Negotiations || !Array.isArray(row.Negotiations)) return false;
    
    const sortedRounds = [...row.Negotiations].sort((a, b) => (b.round || 0) - (a.round || 0));
    
    for (const round of sortedRounds) {
      if (round.offers && Array.isArray(round.offers)) {
        const townOffer = round.offers.find(offer => offer.OfferedBy === 'Town');
        if (townOffer) {
          return townOffer.Response && townOffer.Response.toLowerCase() === 'withdraw';
        }
      }
    }
    return false;
  })();
  
  // Use _isSettled instead of isActive so unfiled cases are not labeled "Settled".
  const isSettled = row._isSettled || row.StipFinalized || row.AwaitingDecision || false;
  const notBarFiled = !row.MasterPetition && !row.barFiled;
  const inactiveLikeSettled = isSettled || notBarFiled;
  const inactiveTooltip = notBarFiled && !isSettled
    ? 'Case not BAR filed'
    : row.AwaitingDecision
      ? 'Awaiting decision'
      : row.StipFinalized
        ? 'Countersigned'
        : 'Case settled';

  // Settled, awaiting decision, or not BAR filed: show the value, not an editable unsent offer.
  if (inactiveLikeSettled && (party === 'Aventine' || party === 'Town')) {
    const hasValue = mostRecentOffer !== null && mostRecentOffer !== undefined && mostRecentOffer > 0;

    // Calculate display value - handle both AV and MV modes for settled and active cases
    let displayValue = mostRecentOffer;

    if (hasValue) {
      // Use persisted state values for EQ and RAR instead of row values
      // IMPORTANT: Explicitly check avToggleActive prop (not persisted state)
      if (avToggleActive === true) {
        // AV mode: display AV values directly
        displayValue = mostRecentOffer;
      } else {
        // MV mode: convert AV to MV for display
        displayValue = convertAVToMV(mostRecentOffer, row.EQ, row.RAR);
      }
    }

    const formattedOffer = hasValue
      ? (avToggleActive === true
        ? `${Math.round(Number(displayValue)).toLocaleString()} AV`
        : `$${(Math.round(Number(displayValue) / 10) * 10).toLocaleString()}`)
      : '';
    const inactiveLabel = notBarFiled && !isSettled
      ? (formattedOffer || (townView ? '' : 'None'))
      : row.AwaitingDecision
        ? formattedOffer
        : row.StipFinalized
          ? (formattedOffer || 'Countersigned')
          : (isWithdraw ? 'Withdraw' : (formattedOffer || 'Settled'));

    return (
      <Tooltip title={inactiveTooltip} placement="top" disableInteractive>
        <span style={{
          fontStyle: 'normal',
          cursor: 'not-allowed',
          fontWeight: 'bold',
        }}>
          {inactiveLabel}
        </span>
      </Tooltip>
    );
  }
  
  // Check if offer is finalized (only for Aventine offers)
  const showsOfferFinalized = party === 'Aventine' && row.statusDisplay === 'Offer Finalized';
  // Check if offer is sent based on negotiations array
  const isOfferSentStatus = !!row._aventineLatestOfferSent;
  
  // If notFiledAtScar, show BARDeterminationValue with color coding (only for Aventine offers)
  if (row.notFiledAtScar && party === 'Aventine') {
    const hasValue = mostRecentOffer !== null && mostRecentOffer !== undefined && mostRecentOffer > 0;
    // Calculate display value - now that we store AV values, convert AV to MV when not in AV mode
    let displayValue = mostRecentOffer;
    // IMPORTANT: Explicitly check avToggleActive prop (not persisted state)
    if (avToggleActive === false && hasValue) {
      displayValue = convertAVToMV(Number(mostRecentOffer), row.EQ, row.RAR);
    }
    
    return (
      <span style={{ 
        ...(row.OfferValueStyle || {}), // Use the style from row creation for color coding
        fontStyle: 'normal', 
        cursor: 'not-allowed',
        fontWeight: 'bold',
        color: hasValue ? 'inherit' : '#666' // Gray color for "None" in BAR only cases
      }}>
        {hasValue ? (avToggleActive === true ? `${Math.round(Number(displayValue)).toLocaleString()} AV` : `$${(Math.round(Number(displayValue) / 10) * 10).toLocaleString()}`) : (townView ? '' : 'None')}
      </span>
    );
  }
  
  // If offer is sent, show with tooltip and cancel button (but keep editable) - only for Aventine
  if ((isOfferSentStatus || showsOfferFinalized) && party === 'Aventine') {
    const hasValue = mostRecentOffer !== null && mostRecentOffer !== undefined && mostRecentOffer > 0;
    
    // Calculate display value - now that we store AV values, convert AV to MV when not in AV mode
    let displayValue = mostRecentOffer;
    // IMPORTANT: Explicitly check avToggleActive prop (not persisted state)
    if (avToggleActive === false && hasValue) {
      displayValue = convertAVToMV(Number(mostRecentOffer), row.EQ, row.RAR);
    }
    
    return (
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        display: 'flex', 
        alignItems: 'center' 
      }}>
        <span 
          style={{ 
            color: 'black', 
            fontStyle: 'normal', 
            cursor: townView ? 'default' : 'text',
            fontWeight: 'bold',
            width: '100%',
          }}
        >
          {hasValue ? (() => {
            const pa = (row.PropertyAssessment || row.AssessmentVillage || 0);
            const equalsAssessment = Math.abs(Number(mostRecentOffer || 0) - pa) <= 1;
            // IMPORTANT: Explicitly check avToggleActive prop (not persisted state)
            if (avToggleActive === true) {
              return `${Math.round(Number(displayValue)).toLocaleString()} AV`;
            } else {
              return `$${(equalsAssessment ? Math.round(Number(displayValue)) : Math.round(Number(displayValue) / 10) * 10).toLocaleString()}`;
            }
          })() : (townView ? '' : 'None')}
        </span>
      </Box>
    );
  }
  
  const hasValue = mostRecentOffer !== null && mostRecentOffer !== undefined && mostRecentOffer > 0;
  
  const hasMismatch = hasRoundMismatch(row.Negotiations);
  const aventineRound = getMostRecentOfferRound(row.Negotiations, 'Aventine');
  const townRound = getMostRecentOfferRound(row.Negotiations, 'Town');
  const isFromPriorRound = townView
    ? (party === 'Town' && isTownOfferFromPriorRound(row.Negotiations, true))
    : (hasMismatch &&
        ((party === 'Aventine' && aventineRound !== null && townRound !== null && aventineRound < townRound) ||
          (party === 'Town' && aventineRound !== null && townRound !== null && townRound < aventineRound)));
  
    // Calculate display value - now that we store AV values, convert AV to MV when not in AV mode
    // Always convert if we have a value and not in AV mode, regardless of hasValue check
    // IMPORTANT: Use the avToggleActive prop explicitly - do not read from persisted state
  let displayValue = mostRecentOffer;
  if (avToggleActive === false && mostRecentOffer !== null && mostRecentOffer !== undefined) {
    // Only convert if value is > 0, otherwise keep as is (null/0)
    if (mostRecentOffer > 0) {
      displayValue = convertAVToMV(Number(mostRecentOffer), row.EQ, row.RAR);
    }
  }
  
  // If this offer is from a prior round, show with info icon and tooltip
  if (isFromPriorRound) {
    const roundNumber = party === 'Aventine' ? aventineRound : townRound;
    return (
      <Tooltip title={`This offer is from the prior round #${roundNumber}`} arrow placement="top">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          cursor: party === 'Town' ? 'pointer' : 'default'
        }}>
          <span style={{ fontWeight: 'bold', marginRight: '4px' }}>
            {isWithdraw ? 'Withdraw' : (hasValue ? (() => {
              const pa = (row.PropertyAssessment || row.AssessmentVillage || 0);
              const equalsAssessment = Math.abs(Number(mostRecentOffer || 0) - pa) <= 1;
              // IMPORTANT: Explicitly check avToggleActive prop (not persisted state)
              if (avToggleActive === true) {
                return `${Math.round(Number(displayValue)).toLocaleString()} AV`;
              } else {
                return `$${(equalsAssessment ? Math.round(Number(displayValue)) : Math.round(Number(displayValue) / 10) * 10).toLocaleString()}`;
              }
            })() : (townView || party === 'Town' ? '' : 'None'))}
          </span>
          <InfoIcon style={{ fontSize: '16px', color: '#999' }} />
        </div>
      </Tooltip>
    );
  }
  
  return (
    <span style={{ 
      color: hasValue || isWithdraw ? 'black' : '#888', 
      fontStyle: 'normal',
      fontWeight: 'bold',
      cursor: party === 'Town' ? 'pointer' : 'default'
    }}>
      {isWithdraw ? 'Withdraw' : (hasValue ? (() => {
        const pa = (row.PropertyAssessment || row.AssessmentVillage || 0);
        // Use mostRecentOffer (AV) for comparison, but displayValue (converted MV) for display
        const equalsAssessment = Math.abs(Number(mostRecentOffer || 0) - pa) <= 1;
        // IMPORTANT: Explicitly check avToggleActive prop (not persisted state)
        if (avToggleActive === true) {
          return `${Math.round(Number(displayValue)).toLocaleString()} AV`;
        } else {
          // MV mode: show with $ prefix
          return `$${(equalsAssessment ? Math.round(Number(displayValue)) : Math.round(Number(displayValue) / 10) * 10).toLocaleString()}`;
        }
      })() : (townView || party === 'Town' ? '' : 'None'))}
    </span>
  );
};

// Export both named and default for backward compatibility
export const AventineOfferCell = (props) => <OfferCell {...props} party="Aventine" />;
export const TownOfferCell = (props) => <OfferCell {...props} party="Town" />;

/**
 * Generalized Edit Cell Component
 * Handles both Aventine and Town offers based on party prop
 */
export const OfferEditCell = ({ 
  params, 
  avToggleActive = false, 
  onShowDialog = null, 
  setOffersChanged = null,
  party = 'Aventine',
  field = null,
  townView = false,
}) => {
  const offerField = field || (party === 'Aventine' ? 'OfferValue' : 'TownOffer');
  const apiRef = useGridApiContext();
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const { user } = useUser();
  
  const mostRecentOffer = parseRowOfferValue(params.row[offerField]);
  
  // Store the original stored value (in AV terms) for comparison
  const originalStoredValueRef = React.useRef(mostRecentOffer);
  
  // Update the ref when the value changes (e.g., from external updates)
  useEffect(() => {
    originalStoredValueRef.current = mostRecentOffer;
  }, [mostRecentOffer]);
  
  // Pass raw AV value to NumericEditCell - it will handle all conversions internally
  // If no offer exists, default to Workup Value (ProprietyWorkup) if available (only for Aventine)
  // Town offers should default to blank (empty string to prevent NumericInputWithValidation from using PropertyAssessment)
  let valueToPass = mostRecentOffer;
  
  if (valueToPass === null || valueToPass === undefined) {
    if (party === 'Aventine') {
      // For Aventine offers, default to workup value if available
      const workupValue = params.row?.ProprietyWorkup;
      if (workupValue != null && workupValue !== undefined && workupValue > 0) {
        // Workup value is in MV terms, so convert to AV for storage
        // Use persisted state values for EQ and RAR instead of row values
        valueToPass = convertMVToAV(workupValue, params.row.EQ, params.row.RAR);
      } else {
        // Fallback to params.value if no workup value exists
        valueToPass = params.value;
      }
    } else {
      // For Town offers, default to empty string (not null) to prevent NumericInputWithValidation 
      // from defaulting to PropertyAssessment/IFMV in MV mode
      valueToPass = '';
    }
  }
  
  // Create update context
  const getUpdateContext = useCallback(
    () => ({
      ...createOfferContext(user, getNegotiationObj, 'Manual'),
      ...(townView ? { townView: true } : {}),
      ...(townView && user?.isLinkAuthenticated
        ? { linkTownSession: linkSessionFromUser(user) }
        : {}),
    }),
    [user, getNegotiationObj, townView],
  );
  
  // Handle value change - intercept the commit to trigger our update
  const handleValueCommit = useCallback((newValue, oldValue) => {
    // Handle null, undefined, empty string, 'None', or 0 values as null
    const newOfferValue = (newValue === null || newValue === undefined || 
                         newValue === '' || newValue === 'None' || 
                         newValue === 0) ? null : Number(newValue);
    
    // Get the case data to extract complete offer information
    const offerCaseIndex = getNegotiationObj.cases.findIndex(
      (caseItem) => caseItem.parcel_id === params.row.parcel_id
    );
    
    if (offerCaseIndex === -1) {
      toast.error(`Failed to update offer: Case not found`);
      // Revert the cell value
      apiRef.current.setEditCellValue({
        id: params.id,
        field: params.field,
        value: oldValue,
      });
      apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
      return;
    }

    const currentCase = getNegotiationObj.cases[offerCaseIndex];
    
    // Use row's filtered negotiations (by EvidenceType) instead of unfiltered case negotiations
    // This ensures we get the correct offer type (Sales vs Assessment)
    const filteredCase = {
      ...currentCase,
      Negotiations: params.row.Negotiations || currentCase.Negotiations
    };
    
    const currentRound = getRowMaxNegotiationRound(filteredCase.Negotiations) || 1;
    const offerInCurrentRound = filteredCase.Negotiations
      ?.find((n) => n.round === currentRound)
      ?.offers?.find((o) => o.OfferedBy === party);

    // Prepare complete offer data for update (preserve same-round offer fields, including Comments)
    const offerData = {
      [offerField]: newOfferValue,
      ...(party === 'Town' && { OfferedBy: 'Town' }),
      ...(offerInCurrentRound && {
        Comments: offerInCurrentRound.Comments,
        Response: offerInCurrentRound.Response,
        OfferDate: offerInCurrentRound.OfferDate,
        Negotiator: offerInCurrentRound.Negotiator,
        UserType: offerInCurrentRound.UserType,
        CaseType: offerInCurrentRound.CaseType,
        LiveOffer: offerInCurrentRound.LiveOffer,
      }),
    };
    
    apiRef.current.stopCellEditMode({ id: params.id, field: params.field });

    const optimisticOverrides = party === 'Aventine'
      ? {
          OfferValue: newOfferValue,
          ...(newOfferValue === null ? { _aventineLatestOfferSent: false } : {}),
        }
      : { TownOffer: newOfferValue };
    applyOfferRowPatch(
      apiRef,
      params.row.parcel_id,
      buildDerivedOfferFieldsForRow(params.row, optimisticOverrides),
    );
    
    updateOfferWithDialog(
      params.row.parcel_id, 
      offerData, 
      getUpdateContext(),
      { updateFrontend: true },
      (result) => {
        if (setOffersChanged) {
          setOffersChanged(true);
        }

        const caseData = usePersistedDataStore.getState().negotiationObj?.cases?.find(
          (c) => c.parcel_id === params.row.parcel_id,
        );
        applyOfferRowPatch(
          apiRef,
          params.row.parcel_id,
          buildOfferRowPatchFromCase(params.row, caseData),
        );
        refreshMunicipalOfferCard(party);
      },
      (error) => {
        logger.error('❌ Failed to update offer value:', error);
        toast.error(`Failed to update offer value: ${error?.error || error?.message || 'Unknown error'}`);
        
        const revertOverrides = party === 'Aventine'
          ? { OfferValue: oldValue }
          : { TownOffer: oldValue };
        applyOfferRowPatch(
          apiRef,
          params.row.parcel_id,
          buildDerivedOfferFieldsForRow(params.row, revertOverrides),
        );
      },
      onShowDialog,
      { [offerField]: oldValue },
      apiRef,
      params.row // This contains filtered negotiations by EvidenceType
    );
  }, [params, getNegotiationObj, getUpdateContext, onShowDialog, setOffersChanged, apiRef, offerField, party]);
  
  const handleCommit = useCallback((newValue, oldValue, meta) => {
    const originalValue = originalStoredValueRef.current;
    if (newValue !== originalValue) {
      originalStoredValueRef.current = newValue;
      handleValueCommit(newValue, originalValue);
    } else {
      apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
    }
  }, [params, apiRef, handleValueCommit]);
  
  return (
    <NumericEditCell 
      {...params} 
      value={valueToPass} 
      avToggleActive={avToggleActive} 
      row={params.row}
      onCommit={handleCommit}
    />
  );
};

// Export both named and default for backward compatibility
export const AventineOfferEditCell = (props) => <OfferEditCell {...props} party="Aventine" />;
export const TownOfferEditCell = (props) => <OfferEditCell {...props} party="Town" />;

export default OfferCell;